import { useEffect, useState, type ComponentType } from 'react';
import { useMediaQuery } from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Pause,
  PlayArrow,
  Search,
  FolderOutlined,
  History,
  Sync,
  ViewKanbanOutlined,
  GraphicEq,
  PeopleOutline,
  ExtensionOutlined,
  Check,
  Download,
  InsertDriveFileOutlined,
} from '@mui/icons-material';
import CardSwap from '@/common/components/reactBits/CardSwap';

const CHAPTERS = [
  {
    label: 'Library',
    icon: FolderOutlined,
    title: 'Your sessions. All together.',
    note: 'Find a project, then open its linked session in your installed DAW.',
    route: 'lp-explore',
  },
  {
    label: 'Versions',
    icon: History,
    title: 'A better idea isn’t always the latest.',
    note: 'Inspect a checkpoint. Download an older copy or sync it back to your folder.',
    route: 'lp-versions',
  },
  {
    label: 'Sync',
    icon: Sync,
    title: 'Save the changes. Keep the context.',
    note: 'Review local file changes and create a version with a commit message.',
    route: 'lp-versions',
  },
  {
    label: 'Boards',
    icon: ViewKanbanOutlined,
    title: 'A place for the bigger picture.',
    note: 'Organize projects into custom stages and move the release forward.',
    route: 'lp-boards',
  },
  {
    label: 'Files',
    icon: GraphicEq,
    title: 'Everything that makes the session.',
    note: 'Browse project files and stem packs. Preview supported audio in the app.',
    route: 'lp-explore',
  },
  {
    label: 'Members',
    icon: PeopleOutline,
    title: 'Good work deserves good company.',
    note: 'Invite collaborators or read-only members. Board access is separate.',
    route: 'lp-faq',
  },
  {
    label: 'Plugins',
    icon: ExtensionOutlined,
    title: 'Know what went into the sound.',
    note: 'Experimental plugin detection by version. Results may be incomplete.',
    route: 'lp-explore',
  },
];

function DemoLibrary() {
  const [query, setQuery] = useState('');
  const projects = [
    ['Nightdrive', 'Ableton Live', '14'],
    ['Coastline', 'Logic Pro', '3'],
    ['Room Tone 04', 'REAPER', '2'],
    ['Paperweight', 'Studio One', '18'],
  ];
  const filtered = projects.filter(([name]) =>
    name.toLowerCase().includes(query.toLowerCase())
  );
  return (
    <>
      <div className="lp-demo-title">
        <h3>Projects</h3>
        <span>4 projects</span>
      </div>
      <label className="lp-demo-search">
        <Search fontSize="small" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a project…"
          aria-label="Search demo projects"
        />
        <kbd>⌕</kbd>
      </label>
      <div className="lp-demo-projects">
        {filtered.map(([name, daw, versions], i) => (
          <div className="lp-demo-project" key={name}>
            <span className={`lp-demo-project-icon tone-${i}`}>
              <FolderOutlined />
            </span>
            <strong>{name}</strong>
            <span>{daw}</span>
            <small>{versions} versions</small>
          </div>
        ))}
      </div>
      {!filtered.length && (
        <p className="lp-demo-muted">No sample projects match that search.</p>
      )}
      <div className="lp-demo-bottom">
        <span>Project folders & stem packs</span>
        <span>Grid view</span>
      </div>
    </>
  );
}

function DemoVersions({ previewing }: { previewing: boolean }) {
  const [version, setVersion] = useState(14);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!previewing) return;
    const reset = window.setTimeout(() => setVersion(14), 0);
    const timer = window.setTimeout(() => setVersion(12), 2200);
    return () => {
      window.clearTimeout(reset);
      window.clearTimeout(timer);
    };
  }, [previewing]);
  return (
    <>
      <div className="lp-demo-title">
        <h3>Version History</h3>
        <span>14 versions</span>
      </div>
      {[
        ['14', 'Vocal comp + new bridge', '3 created · 5 updated'],
        ['13', 'Drum bus rebuild', '1 created · 3 updated'],
        ['12', 'Arrangement lock', '2 created · 4 updated · 1 deleted'],
      ].map(([v, title, changes]) => (
        <button
          className={`lp-demo-version ${version === +v ? 'is-selected' : ''}`}
          onClick={() => {
            setVersion(+v);
            setNotice('');
          }}
          key={v}
          aria-pressed={version === +v}
        >
          <span className="lp-demo-version-number">v{v}</span>
          <span>
            <strong>{title}</strong>
            <small>{changes}</small>
          </span>
          {v === '14' ? <em>Latest</em> : <History fontSize="small" />}
        </button>
      ))}
      <div className="lp-demo-recovery">
        <div>
          <strong>Go back to v{version}</strong>
          <small>Sync asks before replacing local files.</small>
        </div>
        <button
          className="lp-demo-action"
          onClick={() =>
            setNotice(
              `Demo: Download opens a destination picker for v${version}. No files are downloaded here.`
            )
          }
        >
          <Download fontSize="inherit" /> Download
        </button>
        <button
          className="lp-demo-action"
          onClick={() =>
            setNotice(
              `Demo: syncing v${version} requires confirmation. Newer local files can be overwritten and local-only files deleted. No files are changed here.`
            )
          }
        >
          <Sync fontSize="inherit" /> Sync
        </button>
      </div>
      <p className="lp-demo-notice" role="status">
        {notice || 'Try selecting an older version.'}
      </p>
    </>
  );
}

function DemoSync({ previewing }: { previewing: boolean }) {
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!previewing) return;
    const reset = window.setTimeout(() => setSaved(false), 0);
    const timer = window.setTimeout(() => setSaved(true), 2500);
    return () => {
      window.clearTimeout(reset);
      window.clearTimeout(timer);
    };
  }, [previewing]);
  const [message, setMessage] = useState('New bridge, same late-night feeling');
  return (
    <>
      <div className="lp-demo-title">
        <h3>Local changes</h3>
        <span>Nightdrive</span>
      </div>
      <div className="lp-demo-change">
        <span className="lp-demo-badge">NEW</span>
        <span>Samples / bridge.wav</span>
        <span>Audio</span>
      </div>
      <div className="lp-demo-change">
        <span className="lp-demo-badge blue">UPDATED</span>
        <span>Nightdrive.als</span>
        <span>Session</span>
      </div>
      <div className="lp-demo-change">
        <span className="lp-demo-badge blue">UPDATED</span>
        <span>Renders / rough-master.wav</span>
        <span>Audio</span>
      </div>
      <label className="lp-demo-message">
        Commit message
        <input
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            setSaved(false);
          }}
          aria-label="Demo commit message"
        />
      </label>
      <button
        className="lp-demo-action primary"
        disabled={!message.trim()}
        onClick={() => setSaved(!saved)}
      >
        {saved ? <Check fontSize="small" /> : <Sync fontSize="small" />}
        {saved ? 'Checkpoint preview created' : 'Try creating a version'}
      </button>
      <p className="lp-demo-notice" role="status">
        {saved
          ? 'Demo only. In the app, new and updated files upload to the project.'
          : 'Only new and updated files need uploading. Unchanged files stay put.'}
      </p>
    </>
  );
}

function DemoBoard({ previewing }: { previewing: boolean }) {
  const [stage, setStage] = useState(1);
  useEffect(() => {
    if (!previewing) return;
    const reset = window.setTimeout(() => setStage(1), 0);
    const timer = window.setTimeout(() => setStage(2), 2500);
    return () => {
      window.clearTimeout(reset);
      window.clearTimeout(timer);
    };
  }, [previewing]);
  return (
    <>
      <div className="lp-demo-title">
        <h3>After hours — EP</h3>
        <span>Shared board</span>
      </div>
      <div className="lp-demo-board">
        {['Writing', 'Mixing', 'Mastered'].map((name, i) => (
          <div className="lp-demo-column" key={name}>
            <div className="lp-demo-column-title">
              <i />
              {name}
              <span>{i === stage ? 2 : 1}</span>
            </div>
            <div className="lp-demo-board-card">
              <FolderOutlined fontSize="small" />
              <strong>{['Room Tone 04', 'Coastline', 'Paperweight'][i]}</strong>
              <small>{[2, 3, 18][i]} versions</small>
              <footer>
                <b>{['AR', 'JD', 'MK'][i]}</b>
                {['Ari Reyes', 'Jonah Dey', 'Mara Kade'][i]}
              </footer>
            </div>
            {stage === i && (
              <div className="lp-demo-board-card featured">
                <FolderOutlined fontSize="small" />
                <strong>Nightdrive</strong>
                <small>14 versions</small>
                <footer>
                  <b>MK</b>Mara Kade
                </footer>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="lp-demo-bottom">
        <span>Custom columns · project cards</span>
        <button
          className="lp-demo-action"
          onClick={() => setStage((stage + 1) % 3)}
        >
          Move Nightdrive <ArrowForward fontSize="inherit" />
        </button>
      </div>
    </>
  );
}

function DemoFiles() {
  const [selected, setSelected] = useState('rough-master.wav');
  return (
    <>
      <div className="lp-demo-title">
        <h3>Files</h3>
        <span>Nightdrive · v14</span>
      </div>
      <div className="lp-demo-change">
        <InsertDriveFileOutlined fontSize="small" />
        <span>Nightdrive.als</span>
        <span>Session</span>
      </div>
      <p className="lp-demo-muted">Renders /</p>
      {['rough-master.wav', 'instrumental.wav'].map((name) => (
        <button
          key={name}
          className={`lp-demo-file ${name === selected ? 'is-selected' : ''}`}
          onClick={() => setSelected(name)}
        >
          <GraphicEq fontSize="small" />
          {name}
          <span>WAV</span>
        </button>
      ))}
      <div className="lp-demo-waveform" aria-hidden="true">
        {Array.from({ length: 56 }, (_, i) => (
          <i key={i} style={{ height: `${12 + ((i * 31 + 7) % 56)}%` }} />
        ))}
      </div>
      <div className="lp-demo-bottom">
        <strong>{selected}</strong>
        <span>Audio preview illustration</span>
      </div>
      <p className="lp-demo-notice">
        Select a file to explore. No audio plays in this demo.
      </p>
    </>
  );
}

function DemoMembers() {
  const [role, setRole] = useState('Read-only');
  return (
    <>
      <div className="lp-demo-title">
        <h3>Project members</h3>
        <span>Nightdrive</span>
      </div>
      {[
        ['MK', 'Mara Kade', 'Owner'],
        ['AR', 'Ari Reyes', 'Collaborator'],
        ['JD', 'Jonah Dey', role],
      ].map(([initials, name, memberRole]) => (
        <div className="lp-demo-member" key={name}>
          <b>{initials}</b>
          <span>
            <strong>{name}</strong>
            <small>
              {memberRole === 'Read-only'
                ? 'Can view and download'
                : memberRole === 'Owner'
                  ? 'Manages project access'
                  : 'Can contribute versions'}
            </small>
          </span>
          <em>{memberRole}</em>
        </div>
      ))}
      <div className="lp-demo-bottom">
        <span>Try a member role</span>
        <button
          className="lp-demo-action"
          onClick={() =>
            setRole(role === 'Read-only' ? 'Collaborator' : 'Read-only')
          }
        >
          {role === 'Read-only' ? 'Show collaborator' : 'Show read-only'}{' '}
          <ArrowForward fontSize="inherit" />
        </button>
      </div>
      <p className="lp-demo-notice">
        Sample members. No invitations or permission changes are sent.
      </p>
    </>
  );
}

function DemoPlugins() {
  return (
    <>
      <div className="lp-demo-title">
        <h3>Plugins</h3>
        <span className="lp-demo-badge">ALPHA</span>
      </div>
      <div className="lp-demo-plugin-heading">
        <ExtensionOutlined />
        <div>
          <strong>A little more session context.</strong>
          <p>Detection reports are grouped by plugin format.</p>
        </div>
      </div>
      {[
        'Detected plugin names',
        'Vendor and format',
        'Confidence and detection count',
      ].map((name, i) => (
        <div className="lp-demo-change" key={name}>
          <ExtensionOutlined fontSize="small" />
          <span>{name}</span>
          <span>{['VST3', 'AU', 'VST2'][i]}</span>
        </div>
      ))}
      <p className="lp-demo-disclaimer">
        Experimental detection may be incomplete or inaccurate. Collaborators
        still need their own plugins and licenses.
      </p>
    </>
  );
}

const SCENES: ComponentType<{ previewing: boolean }>[] = [
  DemoLibrary,
  DemoVersions,
  DemoSync,
  DemoBoard,
  DemoFiles,
  DemoMembers,
  DemoPlugins,
];

export function LandingProductDemo() {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const select = (index: number) => {
    setActive(index);
    setPlaying(false);
  };
  const chapter = CHAPTERS[active];
  return (
    <div
      className="lp-product-demo"
      role="region"
      aria-label="Interactive StudioVault product tour"
      aria-roledescription="carousel"
    >
      <div className="lp-demo-overline">
        <span>THE WORKSPACE, IN MOTION</span>
        <span>INTERACTIVE DEMO</span>
      </div>
      <CardSwap
        activeIndex={active}
        onChange={setActive}
        playing={playing}
        delay={5500}
      >
        {SCENES.map((Scene, index) => (
          <div
            className="lp-demo-window"
            key={CHAPTERS[index].label}
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${SCENES.length}: ${CHAPTERS[index].label}`}
          >
            <div className="lp-demo-window-bar">
              <span className="lp-demo-window-dots">
                <i />
                <i />
                <i />
              </span>
              <span>
                StudioVault <span className="lp-demo-window-divider">/</span>{' '}
                {CHAPTERS[index].label}
              </span>
              <span className="lp-demo-sample">SAMPLE DATA</span>
            </div>
            <div
              className="lp-demo-window-body"
              onPointerDownCapture={() => setPlaying(false)}
              onFocusCapture={() => setPlaying(false)}
            >
              <Scene
                previewing={index === active && playing && !reducedMotion}
              />
            </div>
          </div>
        ))}
      </CardSwap>
      <div className="lp-demo-caption" aria-live={playing ? 'off' : 'polite'}>
        <div>
          <h2>{chapter.title}</h2>
          <p>{chapter.note}</p>
        </div>
        <a
          href={`#${chapter.route}`}
          aria-label={`Learn more about ${chapter.label.toLowerCase()}`}
        >
          <ArrowForward />
        </a>
      </div>
      <div className="lp-demo-controls">
        <div className="lp-demo-chapters" aria-label="Choose a demo">
          {CHAPTERS.map(({ label, icon: Icon }, index) => (
            <button
              key={label}
              onClick={() => select(index)}
              aria-pressed={active === index}
              aria-label={`Show ${label.toLowerCase()} demo`}
            >
              <Icon />
              <span>{label}</span>
            </button>
          ))}
        </div>
        <div className="lp-demo-transport">
          <button
            aria-label="Previous demo"
            onClick={() => select((active + SCENES.length - 1) % SCENES.length)}
          >
            <ArrowBack />
          </button>
          <button
            aria-label={playing ? 'Pause product tour' : 'Play product tour'}
            aria-pressed={playing}
            onClick={() => setPlaying(!playing)}
          >
            {playing ? <Pause /> : <PlayArrow />}
          </button>
          <button
            aria-label="Next demo"
            onClick={() => select((active + 1) % SCENES.length)}
          >
            <ArrowForward />
          </button>
        </div>
      </div>
    </div>
  );
}
